import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Car, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      let redirectTo = location.state?.from;
      if (!redirectTo && res.user.role !== "admin") {
        // No explicit destination — respect the account's last-known mode
        // or a pending guest hosting intent, instead of always defaulting
        // to the customer homepage. Admins are excluded — they have their
        // own console and should never be auto-routed into hosting mode.
        try {
          const savedMode = localStorage.getItem(`rx_mode_${res.user.id}`);
          const intent = localStorage.getItem("rx_intent");
          if (savedMode === "hosting" || intent === "host") {
            redirectTo = "/owner";
          }
        } catch {}
      }
      redirectTo = redirectTo || "/";
      // Deep-linking into a specific side (owner vs customer) should put
      // the account in the matching mode, so ProtectedRoute/OwnerDashboard's
      // mode-based guard doesn't immediately bounce them back out.
      try {
        if (redirectTo.startsWith("/owner")) {
          localStorage.setItem(`rx_mode_${res.user.id}`, "hosting");
        } else if (redirectTo.startsWith("/account")) {
          localStorage.setItem(`rx_mode_${res.user.id}`, "renting");
        }
        localStorage.removeItem("rx_intent");
      } catch {}
      nav(redirectTo, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-md px-4 py-16">
        <Card className="border-border shadow-organic p-8 fade-up">
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Car className="text-primary-foreground" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-center">Welcome back</h1>
          <p className="text-sm text-muted-foreground text-center mt-1">Sign in to manage your trips and cars.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90 btn-lift" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="text-right mb-2">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-[#2A9D8F]">Forgot password?</Link>
          </div>
          <div className="mt-6 text-sm text-center text-muted-foreground">
            New here? <Link to="/register" state={{ from: location.state?.from }} className="text-primary font-medium hover:underline" data-testid="goto-register">Create an account</Link>
          </div>
        </Card>
      </section>
    </PublicLayout>
  );
}
